import { useRef, useState } from "react";
import { Box, Typography, Chip } from "@mui/joy";
import { Upload } from "lucide-react";

import { Button } from "../../../../../../shared";
import UploadDropzone from "./UploadDropzone";
import AttachmentRow from "./AttachmentRow";
import DeleteAttachmentModal from "./DeleteAttachmentModal";
import useProjectAttachments from "./hooks/useProjectAttachments";

import type { IProjectAsset } from "../../../../../../interfaces";

export default function AdditionalUploads({ projectId }: { projectId?: string }) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [pendingDelete, setPendingDelete] = useState<IProjectAsset | null>(null);

    const {
        attachments,
        uploadQueue,
        isUploading,
        isDeleting,
        uploadFiles,
        downloadAttachment,
        deleteAttachment,
    } = useProjectAttachments(projectId);

    const handleConfirmDelete = async () => {
        if (!pendingDelete) return;
        const deleted = await deleteAttachment(pendingDelete);
        if (deleted) {
            setPendingDelete(null);
        }
    };

    return (
        <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    py: 1.5,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography level="title-md">Additional Uploads</Typography>
                    <Typography level="body-xs" color="neutral">
                        Anything else related to this project, added outside the generated report flow.
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <Chip variant="plain" color="neutral" size="sm">
                        {attachments.length}
                    </Chip>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        disabled={!projectId || isUploading}
                        startDecorator={<Upload size={16} />}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload
                    </Button>
                </Box>
            </Box>

            <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(event) => {
                    void uploadFiles(event.target.files);
                    event.target.value = "";
                }}
            />

            <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
                <UploadDropzone
                    uploadQueue={uploadQueue}
                    onFiles={(files) => void uploadFiles(files)}
                    onBrowse={() => fileInputRef.current?.click()}
                />

                {attachments.map((asset) => (
                    <AttachmentRow
                        key={asset.Id}
                        asset={asset}
                        onDownload={() => void downloadAttachment(asset)}
                        onDelete={() => setPendingDelete(asset)}
                    />
                ))}
            </Box>

            <DeleteAttachmentModal
                attachment={pendingDelete}
                isDeleting={isDeleting}
                onCancel={() => setPendingDelete(null)}
                onConfirm={handleConfirmDelete}
            />
        </Box>
    );
}
